import { inject, injectable } from 'inversify';
import { addHours } from 'date-fns';

import StorageService from '../../StorageService';

import type { BookmarksResponse, FavouritesListResponse, LineupResource, Product } from './types';

@injectable()
export default class StreamIQData {
  protected lineup: LineupResource | undefined = undefined;
  protected favoriteResources: FavouritesListResponse['resources'] = [];
  protected bookmarkResources: BookmarksResponse['resources'] = [];
  protected products: Product[] = [];
  protected purchases: { productId: string; exp: number }[] = [];

  constructor(@inject(StorageService) private readonly storageService: StorageService) {
    // retrieve existing purchases
    this.storageService.getItem('IQ-PPV', true).then((purchases) => {
      if (Array.isArray(purchases)) {
        this.purchases = purchases;
      }
    });
  }

  setLineup(lineup: LineupResource | undefined) {
    this.lineup = lineup;
  }

  getLineup() {
    return this.lineup;
  }

  setFavorites(favorites: FavouritesListResponse['resources']) {
    this.favoriteResources = favorites;
  }

  getFavorites() {
    // epg-service shows get the `tv-show` mediaid prefix to distinguish them from vod-service shows (see StreamIQContentApiService.transformListItem)
    return this.favoriteResources
      .filter((item) => 'resource' in item)
      .map((item) => ({
        ...item,
        mediaid: `${item.resource.serviceName === 'epg-service' && item.resource.type === 'show' ? 'tv-show' : item.resource.type}_${item.resource.id}`,
      }));
  }

  setBookmarks(bookmarks: BookmarksResponse['resources']) {
    this.bookmarkResources = bookmarks;
  }

  getBookmarks() {
    return this.bookmarkResources.filter((item) => 'resource' in item);
  }

  setProducts(products: Product[]) {
    this.products = products;
  }

  getProducts() {
    return this.products;
  }

  registerPurchase(productId: string, hoursAccess: number) {
    this.purchases.push({ productId, exp: addHours(new Date(), hoursAccess).getTime() });

    void this.storageService.setItem('IQ-PPV', JSON.stringify(this.purchases));
  }

  getPurchase(productId: string) {
    return this.purchases.find((purchase) => purchase.productId === productId && purchase.exp > Date.now());
  }
}
